import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_BASEURL; // Update with actual backend URL

// Create an Axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        "Content-Type": "application/json",
    },
});

// Request Interceptor – Attach Token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("token");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response Interceptor – Handle Token Expiry
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If token expired, try refreshing it
        if (localStorage.getItem("token") && error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshResponse = await axios.get(`${API_URL}/refresh-token`, { withCredentials: true });
                localStorage.setItem("token", refreshResponse.data.accessToken);
                api.defaults.headers.common["Authorization"] = `Bearer ${refreshResponse.data.accessToken}`;

                return api(originalRequest); // Retry the original request
            } catch (refreshError) {
                console.error("Token refresh failed:", refreshError);
                localStorage.removeItem("token"); // Remove expired token
                window.location.href = "/login"; // Redirect to login
            }
        }

        return Promise.reject(error);
    }
);

// Generic API Request Handler
export const apiRequest = async (requestFunc) => {
    try {
        const response = await requestFunc();
        return response.data;
    } catch (error) {
        console.error("API Error:", error.response?.data || error.message);
        throw error.response?.data || { message: "An unexpected error occurred." };
    }
};

// 📌 API Endpoints

export const fetchValidQuestions = (quizId) =>
    apiRequest(() => api.get(`/quiz/details/${quizId}`));

export const getQuizList = (params) =>
    apiRequest(() => api.get(`/quiz/list`, { params }));

export const submitAnswers = (payload) =>
    apiRequest(() => api.post(`/quiz/saveAnswer`, payload));

export const startQuiz = (payload) =>
    apiRequest(() => api.post(`/quiz/start`, payload));

export const getResult = (params) =>
    apiRequest(() => api.get(`/quiz/getResults`, { params }));

export const loginTutor = async (payload) => {
    const response = await apiRequest(() => api.post(`/tutors/login`, payload));

    if (response.accessToken) {
        localStorage.setItem("token", response.accessToken);
        localStorage.setItem("userRole", "tutor");
    }
    return response;
};

export const loginLearner = async (payload) => {
    const response = await apiRequest(() => api.post(`/learner/login`, payload));

    if (response.token) {
        localStorage.setItem("token", response.token);
        localStorage.setItem("userRole", "learner");
    }
    return response;
};

export const getTutorInformation = (tutorId) =>
    apiRequest(() => api.get(`/tutors/${tutorId}`));

export const getLearnerResult = (learnerId) =>
    apiRequest(() => api.get(`/result/learner/${learnerId}`));

export const addQuestionApi = (payload) =>
    apiRequest(() => api.post(`/questions/addQuestion`, payload));

export const getLearnerQuiz = (learnerId) =>
    apiRequest(() => api.get(`/learner/quizzes/${learnerId}`));

export const getLearnersApi = (params) => apiRequest(() => api.get(`/learner/learners`, { params }));

export const createQuiz = (payload) =>
    apiRequest(() => api.post(`/quiz`, payload));

export const generateQuestions = (payload) =>
    apiRequest(() => api.post(`/questions/generate`, payload, {
        maxContentLength: 100 * 1024 * 1024, // 100MB
        maxBodyLength: 100 * 1024 * 1024
    }));

export const learnerRegistration = (payload) =>
    apiRequest(() => api.post(`/learner/learners`, payload));

export const editTutor = (tutorId, payload) =>
    apiRequest(() => api.put(`/tutors/edit/${tutorId}`, payload));

export const importLearners = (payload) =>
    apiRequest(() => api.post(`/learner/import-learners`, payload, {
        maxContentLength: 100 * 1024 * 1024, // 100MB
        maxBodyLength: 100 * 1024 * 1024
    }));